import React, { useEffect, useRef, useState, useCallback } from 'react';
import './ParticleBackground.css';
import Particles from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";

const ParticleBackground = ({ 
  backgroundColor = "#000000", 
  particleColor = "#e32961",
  particleCount = 100,
  showDebug = true
}) => {
  const [scriptError, setScriptError] = useState(null);
  const [particlesReady, setParticlesReady] = useState(false);
  const [componentMounted, setComponentMounted] = useState(false);

  // Check WebGL availability
  const checkWebGL = () => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      return !!gl;
    } catch (error) {
      return false;
    }
  };

  const [webGLAvailable] = useState(checkWebGL());

  const particlesInit = useCallback(async (engine) => {
    console.log("🎯 particlesInit called with engine:", engine);
    try {
      await loadSlim(engine);
      console.log("✅ loadSlim completed successfully");
    } catch (error) {
      console.error("❌ Error in particlesInit:", error);
      setScriptError("Init error: " + error.message);
    }
  }, []);

  const particlesLoaded = useCallback((container) => {
    console.log("🎯 particlesLoaded called with container:", container);
    if (container) {
      setParticlesReady(true);
      console.log("✅ Particles are now ready");
      console.log("Particle count:", container.particles?.count);
      console.log("Container canvas:", container.canvas);
      console.log("Container canvas element:", container.canvas?.element);
      
      // Force refresh after loading
      setTimeout(() => {
        container.refresh();
        console.log("🔄 Container refreshed");
      }, 100);
    }
  }, []);

  const particlesOptions = {
    "autoPlay": true,
    "background": {
      "color": {
        "value": backgroundColor
      },
      "image": "",
      "position": "",
      "repeat": "",
      "size": "",
      "opacity": 1
    },
    "backgroundMask": {
      "composite": "destination-out",
      "cover": {
        "opacity": 1
      },
      "enable": false
    },
    "clear": true,
    "defaultThemes": {},
    "delay": 0,
    "fullScreen": {
      "enable": false,
      "zIndex": 0
    },
    "detectRetina": true,
    "duration": 0,
    "fpsLimit": 120,
    "interactivity": {
      "detectsOn": "window",
      "events": {
        "onClick": {
          "enable": true,
          "mode": "push"
        },
        "onDiv": {
          "selectors": [],
          "enable": false,
          "mode": [],
          "type": "circle"
        },
        "onHover": {
          "enable": true,
          "mode": "grab",
          "parallax": {
            "enable": true,
            "force": 60,
            "smooth": 10
          }
        },
        "resize": {
          "delay": 0.5,
          "enable": true
        }
      },
      "modes": {
        "grab": {
          "distance": 400,
          "links": {
            "blink": false,
            "consent": false,
            "opacity": 1
          }
        },
        "push": {
          "default": true,
          "groups": [],
          "quantity": 4
        }
      }
    },
    "manualParticles": [],
    "particles": {
      "color": {
        "value": particleColor,
        "animation": {
          "h": {
            "count": 0,
            "enable": false,
            "speed": 1,
            "decay": 0,
            "delay": 0,
            "sync": true,
            "offset": 0
          },
          "s": {
            "count": 0,
            "enable": false,
            "speed": 1,
            "decay": 0,
            "delay": 0,
            "sync": true,
            "offset": 0
          },
          "l": {
            "count": 0,
            "enable": false,
            "speed": 1,
            "decay": 0,
            "delay": 0,
            "sync": true,
            "offset": 0
          }
        }
      },
      "move": {
        "angle": {
          "offset": 0,
          "value": 90
        },
        "attract": {
          "distance": 200,
          "enable": false,
          "rotate": {
            "x": 3000,
            "y": 3000
          }
        },
        "center": {
          "x": 50,
          "y": 50,
          "mode": "percent",
          "radius": 0
        },
        "decay": 0,
        "distance": {},
        "direction": "none",
        "drift": 0,
        "enable": true,
        "gravity": {
          "acceleration": 9.81,
          "enable": false,
          "inverse": false,
          "maxSpeed": 50
        },
        "path": {
          "clamp": true,
          "delay": {
            "value": 0
          },
          "enable": false,
          "options": {}
        },
        "outModes": {
          "default": "out"
        },
        "random": false,
        "size": false,
        "speed": 2,
        "spin": {
          "acceleration": 0,
          "enable": false
        },
        "straight": false,
        "trail": {
          "enable": false,
          "length": 10,
          "fill": {}
        },
        "vibrate": false,
        "warp": false
      },
      "number": {
        "density": {
          "enable": true,
          "width": 1920,
          "height": 1080
        },
        "limit": {
          "mode": "delete",
          "value": 0
        },
        "value": particleCount
      },
      "opacity": {
        "value": {
          "min": 0.1,
          "max": 0.5
        },
        "animation": {
          "count": 0,
          "enable": true,
          "speed": 3,
          "decay": 0,
          "delay": 0,
          "sync": false,
          "mode": "auto",
          "startValue": "random",
          "destroy": "none"
        }
      },
      "reduceDuplicates": false,
      "shadow": {
        "blur": 0,
        "color": {
          "value": "#000"
        },
        "enable": false,
        "offset": {
          "x": 0,
          "y": 0
        }
      },
      "shape": {
        "close": true,
        "fill": true,
        "options": {},
        "type": "circle"
      },
      "size": {
        "value": {
          "min": 1,
          "max": 10
        },
        "animation": {
          "count": 0,
          "enable": true,
          "speed": 20,
          "decay": 0,
          "delay": 0,
          "sync": false,
          "mode": "auto",
          "startValue": "random",
          "destroy": "none"
        }
      },
      "stroke": {
        "width": 0
      },
      "zIndex": {
        "value": 0,
        "opacityRate": 1,
        "sizeRate": 1,
        "velocityRate": 1
      },
      "destroy": {
        "bounds": {},
        "mode": "none",
        "split": {
          "count": 1,
          "factor": {
            "value": 3
          },
          "rate": {
            "value": {
              "min": 4,
              "max": 9
            }
          },
          "sizeOffset": true
        }
      },
      "roll": {
        "darken": {
          "enable": false,
          "value": 0
        },
        "enable": false,
        "enlighten": {
          "enable": false,
          "value": 0
        },
        "mode": "vertical",
        "speed": 25
      },
      "tilt": {
        "value": 0,
        "animation": {
          "enable": false,
          "speed": 0,
          "decay": 0,
          "sync": false
        },
        "direction": "clockwise",
        "enable": false
      },
      "twinkle": {
        "lines": {
          "enable": false,
          "frequency": 0.05,
          "opacity": 1
        },
        "particles": {
          "enable": false,
          "frequency": 0.05,
          "opacity": 1
        }
      },
      "wobble": {
        "distance": 5,
        "enable": false,
        "speed": {
          "angle": 50,
          "move": 10
        }
      },
      "life": {
        "count": 0,
        "delay": {
          "value": 0,
          "sync": false
        },
        "duration": {
          "value": 0,
          "sync": false
        }
      },
      "rotate": {
        "value": 0,
        "animation": {
          "enable": false,
          "speed": 0,
          "decay": 0,
          "sync": false
        },
        "direction": "clockwise",
        "path": false
      },
      "orbit": {
        "animation": {
          "count": 0,
          "enable": false,
          "speed": 1,
          "decay": 0,
          "delay": 0,
          "sync": false
        },
        "enable": false,
        "opacity": 1,
        "rotation": {
          "value": 45
        },
        "width": 1
      },
      "links": {
        "blink": false,
        "color": {
          "value": particleColor
        },
        "consent": false,
        "distance": 150,
        "enable": true,
        "frequency": 1,
        "opacity": 0.4,
        "shadow": {
          "blur": 5,
          "color": {
            "value": "#000"
          },
          "enable": false
        },
        "triangles": {
          "enable": false,
          "frequency": 1
        },
        "width": 1,
        "warp": false
      },
      "repulse": {
        "value": 0,
        "enabled": false,
        "distance": 1,
        "duration": 1,
        "factor": 1,
        "speed": 1
      }
    },
    "pauseOnBlur": true,
    "pauseOnOutsideViewport": true,
    "responsive": [],
    "smooth": false,
    "style": {},
    "themes": [],
    "zLayers": 100,
    "key": "parallax",
    "name": "Parallax",
    "motion": {
      "disable": false,
      "reduce": {
        "factor": 4,
        "value": true
      }
    }
  };

  useEffect(() => {
    console.log("FluidSimulation component mounted");
    setComponentMounted(true);
    
    // Test if Particles component exists
    console.log("Particles component:", Particles);
    console.log("loadSlim function:", loadSlim);
  }, []);

  useEffect(() => {
    const initParticles = async () => {
      try {
        console.log("🔄 Starting particle initialization...");
        
        // Import modules
        const { loadSlim } = await import("@tsparticles/slim");
        const { tsParticles } = await import("@tsparticles/engine");
        
        // Initialize engine
        await loadSlim(tsParticles);
        console.log("✅ Engine loaded");
        
        // Load particles
        const container = await tsParticles.load("tsparticles", {
          background: {
            color: {
              value: backgroundColor
            }
          },
          fpsLimit: 120,
          interactivity: {
            events: {
              onClick: {
                enable: true,
                mode: "push"
              },
              onHover: {
                enable: true,
                mode: "grab"
              },
              resize: true
            },
            modes: {
              push: {
                quantity: 4
              },
              grab: {
                distance: 400,
                links: {
                  opacity: 1
                }
              }
            }
          },
          particles: {
            color: {
              value: particleColor
            },
            links: {
              color: particleColor,
              distance: 150,
              enable: true,
              opacity: 0.4,
              width: 1
            },
            move: {
              direction: "none",
              enable: true,
              outModes: {
                default: "out"
              },
              random: false,
              speed: 2,
              straight: false
            },
            number: {
              density: {
                enable: true,
                width: 1920,
                height: 1080
              },
              value: particleCount
            },
            opacity: {
              value: {
                min: 0.1,
                max: 0.5
              },
              animation: {
                enable: true,
                speed: 3,
                mode: "auto",
                startValue: "random"
              }
            },
            shape: {
              type: "circle"
            },
            size: {
              value: {
                min: 1,
                max: 10
              },
              animation: {
                enable: true,
                speed: 20,
                mode: "auto",
                startValue: "random"
              }
            }
          },
          detectRetina: true
        });

        if (container) {
          console.log("✅ Particles loaded successfully");
          console.log("📊 Particle count:", container.particles?.count);
          setParticlesReady(true);
        }
        
      } catch (error) {
        console.error("❌ Particle initialization failed:", error);
        setScriptError("Init failed: " + error.message);
      }
    };

    initParticles();
  }, [backgroundColor, particleColor, particleCount]);

  return (
    <div className="particle-background-container">
      
      {/* Debug info */}
      {/* {showDebug && (
        <div className="particle-debug-info">
          <div>Component Mounted: {componentMounted ? 'Yes' : 'No'}</div>
          <div>Particles Ready: {particlesReady ? 'Yes' : 'No'}</div>
          <div>WebGL Available: {webGLAvailable ? 'Yes' : 'No'}</div>
          <div>Using: TSParticles React</div>
          {scriptError && <div style={{color: 'red'}}>Error: {scriptError}</div>}
        </div>
      )} */}
      
      {/* TSParticles React Component */}
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={particlesOptions}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

export default ParticleBackground;